import React, { Fragment, useRef, useState, useEffect } from 'react'
import {
  Dialog, DialogPanel, DialogTitle,
  Transition, TransitionChild,
  Listbox, ListboxButton, ListboxOptions, ListboxOption, Label
} from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import AutoPayOffsetSelector from './AutoPayOffsetSelector';
import AddPaymentMethod from './AddPaymentMethod';
// import Turbolinks from 'turbolinks'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

function Model({ isOpen, setOpen,
  actionUrl, paymentMethods, selectedPaymentMethod, setSelectedPaymentMethod, fundRruleIcal, initialAutopayRruleIcal,
  stripePublishableKey,
  customer,
  createCardPath,
  invoiceGenerationLeadTimeDays,
  hasExistingAutopay,
  cancelAutopayUrl,
  nextAutopayDate,
  autopayAmount
}) {

  const cancelButtonRef = useRef(null)
  const [currentAutopayIcal, setCurrentAutopayIcal] = useState(initialAutopayRruleIcal || '');
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [showStopAutopayConfirmation, setShowStopAutopayConfirmation] = useState(false);

  const handleStopAutopay = async () => {
    try {
      const response = await fetch(cancelAutopayUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Reload the page to reflect changes
        window.location.reload();
      } else {
        console.error('Failed to stop autopay');
      }
    } catch (error) {
      console.error('Error stopping autopay:', error);
    }
  };

  return (
    <Transition as="div" show={isOpen}>
      <Dialog className="relative z-10" onClose={setOpen} initialFocus={cancelButtonRef}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {showStopAutopayConfirmation ? (
                  <div className="text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 sm:mt-5">
                      <DialogTitle as="h3" className="text-lg text-center font-medium leading-6 text-gray-900">
                        Stop AutoPay
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to stop AutoPay? You'll need to make manual payments going forward.
                        </p>
                        {nextAutopayDate && (
                          <p className="text-sm text-gray-600 mt-2">
                            Your next scheduled payment of <span className="font-medium">{autopayAmount}</span> on <span className="font-medium">{nextAutopayDate}</span> will be cancelled.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                        onClick={handleStopAutopay}
                      >
                        Stop AutoPay
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                        onClick={() => setShowStopAutopayConfirmation(false)}
                        ref={cancelButtonRef}
                      >
                        Keep AutoPay
                      </button>
                    </div>
                  </div>
                ) : paymentMethods.length == 0 && !showAddPaymentMethod ? (
                  <div className="text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                      <PlusIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 sm:mt-5">
                      <DialogTitle as="h3" className="text-lg text-center font-medium leading-6 text-gray-900">
                        Add Payment Method First
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You need to add a payment method before setting up AutoPay. Once you add a card, you'll be able to schedule automatic payments.
                        </p>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                        onClick={() => setShowAddPaymentMethod(true)}
                      >
                        Add Payment Method
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                        onClick={() => setOpen(false)}
                        ref={cancelButtonRef}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : showAddPaymentMethod ? (
                  <div>
                    <div className="flex items-center mb-4">
                      <button
                        type="button"
                        onClick={() => setShowAddPaymentMethod(false)}
                        className="rounded-md p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <span className="sr-only">Go back</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900 ml-2">
                        Add Payment Method
                      </DialogTitle>
                    </div>
                    <AddPaymentMethod
                      stripe_publishable_key={stripePublishableKey}
                      customer={customer}
                      showCard={true}
                      create_card_path={createCardPath}
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 sm:mt-5">
                        <DialogTitle as="h3" className="text-lg text-center font-medium leading-6 text-gray-900">
                          {hasExistingAutopay ? 'Manage AutoPay' : 'Setup AutoPay'}
                        </DialogTitle>
                        {hasExistingAutopay && nextAutopayDate && (
                          <div className="mt-2 text-center">
                            <p className="text-sm text-gray-600">
                              Next payment: <span className="font-medium">{autopayAmount}</span> on <span className="font-medium">{nextAutopayDate}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <form action={actionUrl} method="POST" className="mt-4">
                      <input type="hidden" name="authenticity_token" value={document.querySelector("[name='csrf-token']")?.content} />
                      <input type="hidden" name="payment_method" value={selectedPaymentMethod?.id} />

                      <div className="mt-4">
                        <AutoPayOffsetSelector
                          fieldName="autopay_rrule"
                          fundRruleIcal={fundRruleIcal}
                          initialAutopayRruleIcal={initialAutopayRruleIcal}
                          onIcalChange={setCurrentAutopayIcal}
                          invoiceGenerationLeadTimeDays={invoiceGenerationLeadTimeDays}
                        />
                      </div>

                      <div className="mt-4">
                        <Dropdown
                          paymentMethods={paymentMethods}
                          selectedPaymentMethod={selectedPaymentMethod}
                          setSelectedPaymentMethod={setSelectedPaymentMethod}
                        />
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-3 sm:gap-3">
                        <button
                          type="submit"
                          disabled={!currentAutopayIcal || !selectedPaymentMethod}
                          className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm disabled:opacity-50"
                        >
                          {hasExistingAutopay ? 'Update AutoPay' : 'Create AutoPay'}
                        </button>
                        {hasExistingAutopay && (
                          <button
                            type="button"
                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:col-start-1 sm:text-sm"
                            onClick={() => setShowStopAutopayConfirmation(true)}
                          >
                            Stop AutoPay
                          </button>
                        )}
                        <button
                          type="button"
                          className={`mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:text-sm ${hasExistingAutopay ? 'sm:col-start-3' : 'sm:col-start-1'}`}
                          onClick={() => setOpen(false)}
                          ref={cancelButtonRef}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

function Dropdown({ paymentMethods, selectedPaymentMethod, setSelectedPaymentMethod }) {

  return (
    <Listbox as="div" value={selectedPaymentMethod} onChange={setSelectedPaymentMethod}>
      <Label className="block text-sm font-medium text-gray-700">Select the payment method you would like to be charged</Label>
      <div className="relative mt-1">

        <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
          <span className="block truncate">{`${selectedPaymentMethod.card.brand} - ending in ${selectedPaymentMethod.card.last4}`}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>
        <Transition
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {paymentMethods.length > 0 ? (
              paymentMethods.map((pm, idx) => (
                <ListboxOption
                  key={idx}
                  className={({ active }) =>
                    classNames(
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                      'relative cursor-default select-none py-2 pl-3 pr-9'
                    )
                  }
                  value={pm}
                >
                  {({ selected, active }) => (
                    <>
                      <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                        {`${pm.card.brand} - ending in ${pm.card.last4}`}
                      </span>

                      {selected ? (
                        <span
                          className={classNames(
                            active ? 'text-white' : 'text-indigo-600',
                            'absolute inset-y-0 right-0 flex items-center pr-4'
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}

                </ListboxOption>
              ))
            ) : ""}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  )
}

const AutoPayModal = (props) => {
  const {
    actionUrl,
    paymentMethods,
    fundRruleIcal,
    initialAutopayRruleIcal,
    stripePublishableKey,
    customer,
    createCardPath,
    invoiceGenerationLeadTimeDays,
    hasExistingAutopay,
    cancelAutopayUrl,
    nextAutopayDate,
    autopayAmount
  } = props;

  const [open, setOpen1] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(paymentMethods?.[0] || null)

  useEffect(() => {
    if (!selectedPaymentMethod && paymentMethods?.length > 0) {
      setSelectedPaymentMethod(paymentMethods[0]);
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const setOpen = (val) => {
    setOpen1(val)
  }

  const buttonText = hasExistingAutopay ? 'Manage AutoPay' : 'Setup Auto Pay';

  return (
    <>
      <div>
        <button
          className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"
          onClick={() => setOpen(true)}
        >
          {buttonText}
        </button>
        <Model
          isOpen={open}
          setOpen={setOpen}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          paymentMethods={paymentMethods}
          actionUrl={actionUrl}
          fundRruleIcal={fundRruleIcal}
          initialAutopayRruleIcal={initialAutopayRruleIcal}
          stripePublishableKey={stripePublishableKey}
          customer={customer}
          createCardPath={createCardPath}
          invoiceGenerationLeadTimeDays={invoiceGenerationLeadTimeDays}
          hasExistingAutopay={hasExistingAutopay}
          cancelAutopayUrl={cancelAutopayUrl}
          nextAutopayDate={nextAutopayDate}
          autopayAmount={autopayAmount}
        />
      </div>
    </>
  );
};

export default AutoPayModal;
